
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */

import { Box, Dialog, DialogContent, DialogTitle, styled, Typography } from '@mui/material';
import {
    Checkbox,
    CollaboratingGroupContext,
    CrfCondition,
    CrfForm,
    CtcaeSelector,
    CtcaeSelectorType,
    DatePicker,
    Field,
    FormBreadcrumbs,
    FormOptionsContext,
    FormSubmitButton,
    GetFieldLookupItem,
    getParentPropertyPath,
    GroupedField,
    GroupedFields,
    GroupedTableArrayField,
    ICrfConditionParameters,
    IGroupedTableArrayFieldColumn,
    Input,
    InstitutionContext,
    MasterGroupContext,
    Numeric,
    OnlinePatientManagementContext,
    PageLayout,
    PatientContext,
    RouteLoading,
    Select,
    Text,
    ToggleButtonGroup
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import { cloneDeep } from 'lodash-es';
import { createContext, FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';


/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { ConditionMode, useFieldActions, useFieldState, useFormActions, useScopedField, ValidateOn } from '@ngt/forms-core';
import * as Dtos from '../../../api/dtos';
import { AppSettings } from '../../../AppSettings';
import useBreastMriLesionLookup from '../../../hooks/lookups/useBreastMriLesionLookup';
import InputDisplay from '../inputs/InputDisplay';
import SelectWithTooltip from '../inputs/SelectWithTooltip';
import FormLookupsModifier from '../utilities/FormLookupsModifierProps';


/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IBreastMRIFormProps { }

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const StyledSubheading = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(1, 2, 1, 2)
}))

const StyledDialogFormTitle = styled(DialogTitle)(({ theme }) => ({
    color: theme.palette.secondary.main,
    borderBottom: 'solid rgb(224, 224, 224) 1px',

    '& *': {
        fontSize: '1.5rem !important'
    },
    
}))

const StyledDialogButton = styled(Box)(({ theme }) => ({
    textAlign: 'right',
    padding: theme.spacing(2),

    '& > :first-of-type': {
        marginRight: theme.spacing(1),
    }
    
}))


/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const additionalLesionsPresent = ({ formState }: ICrfConditionParameters<Dtos.BreastMri>) => formState?.value?.additionalLesionsPresent === Dtos.LookupYesNoType.Yes;
const resolutionOngoing = ({ formState, parentFieldName }: ICrfConditionParameters<Dtos.BreastMri>) => { 
    const regex = /adverseEvents\[(?<Index>[0-9]*)].(?<Field>.*)/;
    const res = regex.exec(parentFieldName ?? "");
    const idx = res?.groups?.['Index'];

    if (!idx) return false;

    const ongoing = formState?.value?.adverseEvents?.[+idx]?.resolutionOngoing;

    return !ongoing;
};
const pathologySpecify = ({ formState, parentFieldName }: ICrfConditionParameters<Dtos.BreastMri>) => { 
    const regex = /breastMriAdditionalLesions\[(?<Index>[0-9]*)]/;
    const res = regex.exec(parentFieldName ?? "");
    const idx = res?.groups?.['Index'];

    if (!idx) return false;

    const pathology = formState?.value?.breastMriAdditionalLesions?.[+idx]?.pathology;


    return pathology === Dtos.LookupPathologyType.Other;
}

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */
const breastMriLesionsKey = "breastMriAdditionalLesions";

export const UK_DATE = {
    mask: [/[0-3U]/, /[0-9K]/, '/', /[0-1U]/, /[0-9K]/, '/', /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/],
    showMask: true
};

const OngoingUpdater: FC = () => {
    const { readOnly } = useContext(FormOptionsContext);
    const { value: ongoing } = useFieldState();
    const { getPropertyPath } = useFieldActions();
    
    const parentPropertyPath = useMemo(() => {
        return getParentPropertyPath(getPropertyPath());
    }, [getPropertyPath]);

    const {setFieldValue} = useFormActions();

    useEffect(() => {
        if (ongoing && !readOnly) {
            setFieldValue(`${parentPropertyPath}.resolutionDate`, undefined, true, true, true, false)
        }
    }, [ongoing, parentPropertyPath, readOnly])

    return null;
}

const AdverseEventColumns: Array<IGroupedTableArrayFieldColumn<Dtos.AdverseEvent>> = [
    {
        propertyPath: 'ctcaeTermId',
        TableCellProps: {
            sx: {
                minWidth: 450,
                maxWidth: 450,
                width: 450,
            }
        },
        content: (
            <Input
                component={CtcaeSelector}
                gradeFieldName="ctcaeGradeId"
                specificationFieldName="ctcaeSpecification"
                type={CtcaeSelectorType.SelectorWithName}
                ctcaeUrl="https://ctcae.breastcancertrials.org.au/"
            />
        )
    },
    {
        propertyPath: 'onsetDate',
        TableCellProps: {
            sx: {
                minWidth: 220,
                maxWidth: 220,
                width: 220,
            }
        },
        content: (
            // <Input
            //     component={Masked}
            //     maskedFormat={UK_DATE}
            // />
            <Input 
                component={DatePicker}
                format="dd/MM/yyyy"
            />
        )
    },
    {
        propertyPath: 'resolutionDate',
        TableCellProps: {
            sx: {
                minWidth: 220,
                maxWidth: 220,
                width: 220,
            }
        },
        content: (
            // <Input
            //     component={Masked}
            //     maskedFormat={UK_DATE}
            // />
            <CrfCondition
                mode={ConditionMode.Enable}
                condition={resolutionOngoing}
                subscription={{ value: true }}
            >
                <Input 
                    component={DatePicker}
                    format="dd/MM/yyyy"
                />
            </CrfCondition>
        )
    },
    {
        propertyPath: 'resolutionOngoing',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100,
            }
        },
        content: (
            <>
                <OngoingUpdater />
                <Input
                    component={Checkbox}
                    label=""
                />
            </>
        )
    },
    {
        propertyPath: 'consequence',
        TableCellProps: {
            sx: {
                minWidth: 200,
                maxWidth: 200,
                width: 200,
            }
        },
        content: (
            <Input
                component={ToggleButtonGroup}
            />
        )
    },
    {
        propertyPath: 'treatmentGiven',
        TableCellProps: {
            sx: {
                minWidth: 200,
                maxWidth: 200,
                width: 200,
            }
        },
        content: (
            <Input
                component={ToggleButtonGroup}
            />
        )
    },
    {
        propertyPath: 'treatmentGivenSpecify',
        TableCellProps: {
            sx: {
                minWidth: 400,
                maxWidth: 400,
                width: 400,
            }
        },
        content: (
            <Input
                component={Text}
            />
        )
    }
];

const BreastMriAdverseEventGrid: FC = () => {
    const {serviceStackClient} = useContext(OnlinePatientManagementContext);

    const getRowName = useCallback(async (value: Dtos.AdverseEvent | null | undefined, _index: number, _values: Dtos.AdverseEvent[] | null | undefined) => {
        if (!value || !value.ctcaeTermId) {
            return undefined;
        }

        try {
            const response = await serviceStackClient.get(new Dtos.GetCtcaeTermName({ ctcaeTermId: value.ctcaeTermId }));

            return response.ctcaeTermName ? `the ${response.ctcaeTermName} adverse event` : undefined;
        }
        catch {
            return undefined;
        }

    }, [serviceStackClient])

    return (
        <>
            <StyledSubheading
                variant="h2"
            >
                Adverse Events related to MRI or biopsy
            </StyledSubheading>
            <GroupedTableArrayField
                propertyPath="adverseEvents"
                columns={AdverseEventColumns}
                entityName='Adverse Event'
                getRowName={getRowName}
                AddButtonProps={{
                    sx: {
                        mb: 2
                    }
                }}
            />
        </>
    );
}

////////////////////////////////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////


interface IBreastMriAdditionalLesionContext {
    index: number | undefined;
    setIndex: (index: number | undefined) => void;
    openSampleDialog: boolean;
    setOpenSampleDialog: (open: boolean) => void;
}

const BreastMriAdditionalLesionContext = createContext<IBreastMriAdditionalLesionContext>({
    setIndex: () => { },
    index: undefined,
    openSampleDialog: false,
    setOpenSampleDialog: () => { }
});

const AdditionalLesionColumns: Array<IGroupedTableArrayFieldColumn<Dtos.BreastMriAdditionalLesion>> = [
    {
        propertyPath: 'lesionNumber',
        TableCellProps: {
            sx: {
                minWidth: 75,
                maxWidth: 75,
                width: 75,
            }
        },
        content: (
            <Typography>
                R<InputDisplay />
            </Typography>
        )
    },
    {
        propertyPath: 'lesionType',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100,
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.lesionType`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'lesionSide',
        TableCellProps: {
            sx: {
                minWidth: 75,
                maxWidth: 75,
                width: 75,
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.lesionSide`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'lesionLocation',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100,
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.lesionLocation`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'lesionSize',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100,
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any) => fieldValue}
            />
        )
    },
    {
        propertyPath: 'birads',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100,
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.birads`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'biopsyMethod',
        TableCellProps: {
            sx: {
                minWidth: 160,
                maxWidth: 160,
                width: 160,
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.biopsyMethod`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'pathology',
        TableCellProps: {
            sx: {
                minWidth: 160,
                maxWidth: 160,
                width: 160,
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.pathology`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'pathologySpecify',
        TableCellProps: {
            sx: {
                minWidth: 120,
                maxWidth: 120,
                width: 120,
            }
        },
        content: (
            <InputDisplay
            convert={(fieldValue: any) => fieldValue}
            />
        )
    },
    {
        propertyPath: 'grade',
        TableCellProps: {
            sx: {
                minWidth: 120,
                maxWidth: 120,
                width: 120,
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.grade`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'estrogenReceptor',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100,
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.estrogenReceptor`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'progesteroneReceptor',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100,
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.progesteroneReceptor`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'her2Ihc',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100,
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.her2Ihc`, fieldValue)?.value}
            />
        )
    },
    {
        propertyPath: 'her2Ish',
        TableCellProps: {
            sx: {
                minWidth: 100,
                maxWidth: 100,
                width: 100,
            }
        },
        content: (
            <InputDisplay
                convert={(fieldValue: any, lookups) => GetFieldLookupItem(lookups, `${breastMriLesionsKey}.her2Ish`, fieldValue)?.value}
            />
        )
    }
];

const InnerForm: FC = () => {
    const { data: patient } = useContext(PatientContext);

    const [index, setIndex] = useState<number | undefined>(undefined);
    const [openSampleDialog, setOpenSampleDialog] = useState<boolean>(false);

    const { state: { value: primaryLesionSide } } = useScopedField('primaryLesionSide', { value: true });
    const { state: { value: parenchymalEnhancementLeftBreast } } = useScopedField('parenchymalEnhancementLeftBreast', { value: true });
    const { state: { value: parenchymalEnhancementRightBreast } } = useScopedField('parenchymalEnhancementRightBreast', { value: true });

    const primaryLesionSideDisabled = useMemo(() => !!primaryLesionSide, [primaryLesionSide]);
    const parenchymalEnhancementLeftBreastDisabled = useMemo(() => !!parenchymalEnhancementLeftBreast, [parenchymalEnhancementLeftBreast]);
    const parenchymalEnhancementRightBreastDisabled = useMemo(() => !!parenchymalEnhancementRightBreast, [parenchymalEnhancementRightBreast]);

    const lesionContext: IBreastMriAdditionalLesionContext = useMemo(() => {
        return {
            index,
            setIndex: (i) => setIndex(i),
            openSampleDialog: openSampleDialog,
            setOpenSampleDialog: (open) => setOpenSampleDialog(open),
        }
    }, [index, setIndex, openSampleDialog, setOpenSampleDialog]);


    const { data: lesionLookup } = useBreastMriLesionLookup(`${breastMriLesionsKey}.breastLesionId`, patient?.id);

    const modifyLookups = useCallback((lookups?: Dtos.ILookup[]) => {
        if (!lesionLookup) {
            return lookups;
        }

        const output: Dtos.ILookup[] = [
            ...(lookups ?? []), 
            lesionLookup
        ];

        return output;
    }, [lesionLookup]);

    return (
        <>
            <GroupedFields>
                <StyledSubheading
                    variant="h2"
                >
                    Summary
                </StyledSubheading>
                <GroupedField
                    propertyPath="mriDatePerformed"
                    component={DatePicker}
                    disabled
                />
            </GroupedFields>
            <GroupedField
                disabled={primaryLesionSideDisabled}
                propertyPath="primaryLesionSide"
                component={ToggleButtonGroup}
            />
            <GroupedField 
                disabled={parenchymalEnhancementLeftBreastDisabled}
                propertyPath="parenchymalEnhancementLeftBreast"
                component={Select}
            />
            <GroupedField 
                disabled={parenchymalEnhancementRightBreastDisabled}
                propertyPath="parenchymalEnhancementRightBreast"
                component={Select}
            />

            <GroupedFields>
                <GroupedField 
                    propertyPath="additionalLesionsPresent"
                    component={ToggleButtonGroup}
                    disabled
                />
                <CrfCondition
                    mode={ConditionMode.Show}
                    condition={additionalLesionsPresent}
                    subscription={{ value: true }}
                >
                    <GroupedField 
                        propertyPath="additionalLesionsPresentTotal"
                        component={Numeric}
                        disabled
                    />
                </CrfCondition>
            </GroupedFields>

            <GroupedField 
                propertyPath="experiencedAdverseEventToMri"
                component={ToggleButtonGroup}
            />

            <GroupedFields>
                <BreastMriAdverseEventGrid />
            </GroupedFields>

            <FormLookupsModifier modifyFn={modifyLookups}>
                <BreastMriAdditionalLesionContext.Provider value={lesionContext}>
                    <BreastMriAddLesionGrid />
                </BreastMriAdditionalLesionContext.Provider>
            </FormLookupsModifier>
        </>
    );
}

const BreastMriAddLesionGrid: FC = () => {
    const { getFieldValue, setFieldValue, submit, getErrors } = useFormActions();

    const { readOnly } = useContext(FormOptionsContext);
    const { index, setIndex, openSampleDialog, setOpenSampleDialog } = useContext(BreastMriAdditionalLesionContext);

    const [previousValue, setPreviousValue] = useState<Dtos.BreastMriAdditionalLesion | undefined>(undefined);

    const { state: { initialValue: initialAdditionalLesions } } = useScopedField(breastMriLesionsKey, { initialValue: true });

    useEffect(() => {
        const v = getFieldValue<Dtos.BreastMriAdditionalLesion>(`${breastMriLesionsKey}[${index}]`);

        setPreviousValue(!v ? undefined : cloneDeep(v));
    }, [index, setPreviousValue, getFieldValue]);

    const hasAdditionalLesions = useMemo(() => {
        return (initialAdditionalLesions?.length ?? 0) > 0;
    }, [initialAdditionalLesions]);

    const onRowClick = useCallback((_value: Dtos.BreastMriAdditionalLesion | undefined | null, index: number, _values: Dtos.BreastMriAdditionalLesion[] | undefined | null) => {
        setPreviousValue(undefined);
        setIndex(index);
        setOpenSampleDialog(true);
    }, [setIndex, setPreviousValue, setOpenSampleDialog]);

    const onOk = useCallback(async () => {
        setOpenSampleDialog(false);
        setPreviousValue(undefined);
        setIndex(undefined);
    }, [setIndex, setPreviousValue, setFieldValue, setOpenSampleDialog, getErrors, submit]);

    const onCancel = useCallback(() => {
        setFieldValue(`${breastMriLesionsKey}[${index}]`, previousValue, true, false, false, false);

        setPreviousValue(undefined);
        setIndex(undefined);
        setOpenSampleDialog(false);
    }, [previousValue, getFieldValue, setFieldValue, index, setIndex, setPreviousValue, setOpenSampleDialog]);

    if (!hasAdditionalLesions) {
        return null;
    }

    return (
        <>
            <StyledSubheading
                variant="h2"
                sx={{ pt: 4 }}
            >
                Additional MRI Lesion/s – Biopsy and Pathology details 
            </StyledSubheading>
            <GroupedTableArrayField
                propertyPath={breastMriLesionsKey}
                columns={AdditionalLesionColumns}
                entityName="Lesion"
                onRowClick={onRowClick}
                hideAddButton
                hideDeletion
            />
            <Dialog
                open={openSampleDialog && index != null}
                onClose={(_event, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                        return;
                    }
                    
                    setOpenSampleDialog(false)
                }}
                aria-labelledby="form-dialog"
                maxWidth="md"
                scroll="body"
                fullWidth
                disableEscapeKeyDown
            >
                <StyledDialogFormTitle>Additional Lesion</StyledDialogFormTitle>
                {
                    index !== null && index !== undefined && (
                        <DialogContent style={{ padding: '0' }}>
                            <Field propertyPath={`${breastMriLesionsKey}[${index}]`}>
                                <GroupedField
                                    propertyPath="lesionNumber"
                                    component={Numeric}
                                    disabled
                                />
                                <GroupedField
                                    propertyPath="lesionType"
                                    component={Select}
                                    disabled
                                />
                                <GroupedField
                                    propertyPath="lesionSide"
                                    component={Select}
                                    disabled
                                />
                                <GroupedField
                                    propertyPath="lesionLocation"
                                    component={Select}
                                    disabled
                                />
                                <GroupedField
                                    propertyPath="lesionSize"
                                    component={Numeric}
                                    disabled
                                />
                                <GroupedField
                                    propertyPath="birads"
                                    component={Select}
                                    disabled
                                />
                                <GroupedField
                                    propertyPath="biopsyMethod"
                                    component={SelectWithTooltip}
                                    tooltipProps={{
                                        arrow: true,
                                        placement: "left",
                                        title: "If no lesion is evident at time of attempted biopsy, the lesion should be reclassified as BIRADS 2 and participant remains potentially eligible"
                                    }}
                                />
                                <GroupedFields>
                                    <GroupedField
                                        propertyPath="pathology"
                                        component={Select}
                                    />
                                    <CrfCondition        
                                        mode={ConditionMode.Show}
                                        condition={pathologySpecify}
                                        subscription={{ value: true }}
                                    >
                                        <GroupedField
                                            propertyPath="pathologySpecify"
                                            component={Text}
                                        />
                                    </CrfCondition>
                                </GroupedFields>
        
                                <GroupedField
                                    propertyPath="grade"
                                    component={Select}
                                />
                                <GroupedField
                                    propertyPath="estrogenReceptor"
                                    component={ToggleButtonGroup}
                                />
                                <GroupedField
                                    propertyPath="progesteroneReceptor"
                                    component={ToggleButtonGroup}
                                />
        
                                <GroupedField
                                    propertyPath="her2Ihc"
                                    component={Select}
                                />
                                <GroupedField
                                    propertyPath="her2Ish"
                                    component={Select}
                                />
                            
                                <StyledDialogButton>
                                    <FormSubmitButton
                                        variant="text"
                                        color="secondary"
                                        onClick={onCancel}
                                    >
                                        {!readOnly ? 'Cancel' : 'Back'}
                                    </FormSubmitButton>
                                    {
                                        !readOnly && (
                                            <FormSubmitButton
                                                variant="text"
                                                color="primary"
                                                onClick={onOk}
                                            >
                                                Ok
                                            </FormSubmitButton>
                                        )
                                    }
                                </StyledDialogButton>
                            </Field>
                        </DialogContent>
                    )
                }
                
            </Dialog>
        </>
    );
}

const BreastMRIForm: FC<IBreastMRIFormProps> = () => {
    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdateCrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsCrfUpdate
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <CrfForm
                validateOn={ValidateOn.onChange}
                readOnly={!canUpdateCrf}
                debug={AppSettings.debug}
                disableEntityTitleCase
                disableEntityLowerCase
            >
                <InnerForm />
            </CrfForm>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default BreastMRIForm;
